import React from "react";
import { Badge, Box, Button, Chip, IconButton, MenuItem, Autocomplete, TextField } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { CircularProgressWithLabel } from "../../../../../../components/widgets/CircularProgressWithLabel";

const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

const convertDateString = (dateString) => {
  const dateObject = new Date(dateString);
  const formattedDate = `${dateObject.getDate()}-${dateObject.toLocaleString(
    "en-US",
    { month: "short" }
  )}-${dateObject.getFullYear()}`;
  return <p>{formattedDate}</p>;
};

const statusColorMap = {
  "Not Started": "default",
  Progress: "primary",
  Hold: "warning",
  Completed: "success",
  Delayed: "error",
};

export const taskListColumns = (
  plant_people,
  handleButtonClick,
  currentUser,
  plantLeads,
  handleCommentDialogOpen,
  tasksData = [],
  fixedType = null,
  mrqList = []
) => {
  // Extract unique task codes from existing tasks
  const existingTaskCodes = (() => {
    if (!Array.isArray(tasksData)) return [];
    const codes = tasksData
      .map((task) => task.taskCode)
      .filter((code) => code && code.trim() !== '')
      .filter((code, index, self) => self.indexOf(code) === index); // Get unique values
    return codes.sort();
  })();

  const mrqTaskCodes = (() => {
    if (!Array.isArray(mrqList)) return [];
    const codes = mrqList
      .map((mrq) => mrq?.mrqNumber)
      .filter((code) => code && String(code).trim() !== '');
    return codes;
  })();

  const taskCodeOptions = Array.from(
    new Set([...existingTaskCodes, ...mrqTaskCodes])
  ).sort();

  return [
  {
    accessorKey: "id",
    header: `ID`,
    enableEditing: false,
  },
  ...(fixedType === "fabrication"
    ? []
    : [
        {
          accessorKey: "taskCode",
          header: `Task Code`,
          size: 120,
          enableEditing: (row) => {
            if (row.original.task === "") return true;
            else if (currentUser)
              return row?.original?.task_members?.includes(currentUser);
            else return false;
          },
          Edit: ({ cell, column, row, table }) => {
            const [value, setValue] = React.useState(cell.getValue() || '');
            
            React.useEffect(() => {
              // Update the row's value cache when value changes
              if (cell.row._valuesCache) {
                cell.row._valuesCache[column.id] = value;
              }
            }, [value, cell, column]);
            
            return (
              <Autocomplete
                freeSolo
                options={taskCodeOptions}
                value={value}
                onChange={(event, newValue) => {
                  const finalValue = typeof newValue === 'string' ? newValue : (newValue?.inputValue || newValue || '');
                  setValue(finalValue);
                  if (cell.row._valuesCache) {
                    cell.row._valuesCache[column.id] = finalValue;
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setValue(newInputValue);
                    if (cell.row._valuesCache) {
                      cell.row._valuesCache[column.id] = newInputValue;
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    fullWidth
                    placeholder="Enter or select task code"
                  />
                )}
                sx={{ minWidth: 120, width: '100%' }}
              />
            );
          },
        },
      ]),
  {
    accessorKey: "type",
    header: `Type`,
    size: 120,
    enableEditing: (row) => {
      // If fixedType is provided, type field is always non-editable
      if (fixedType) {
        return false;
      }
      // Otherwise, use the original logic
      if (row.original.task === "") return true;
      else if (currentUser)
        return row?.original?.task_members?.includes(currentUser);
      else return false;
    },
    muiEditTextFieldProps: () => ({
      children: ["engineering", "procurement", "fabrication"].map((type) => (
        <MenuItem key={type} value={type}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </MenuItem>
      )),
      select: true,
      disabled: !!fixedType, // Disable the field if fixedType is provided
    }),
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <Chip
            label={cell.getValue().charAt(0).toUpperCase() + cell.getValue().slice(1)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ) : (
          <></>
        )}
      </>
    ),
  },
  {
    accessorKey: "task",
    header:
      fixedType === "engineering"
        ? `Task`
        : fixedType === "fabrication"
        ? `Scope`
        : `Action Item`,
    enableEditing: (row) => {
      if (row.original.task === "") return true;
      else if (currentUser)
        return row?.original?.task_members?.includes(currentUser);
      else return false;
    },
    size: 280,
  },
  {
    accessorKey: "startDate",
    header: `Baseline Start`,
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <div style={{ whiteSpace: "wrap" }}>
            {convertDateString(cell.getValue())}
          </div>
        ) : (
          ""
        )}
      </>
    ),
    muiEditTextFieldProps: ({ cell }) => ({
      type: "date",
    }),
  },
  {
    accessorKey: "endDate",
    header: `Baseline End`,
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <div style={{ whiteSpace: "wrap" }}>
            {convertDateString(cell.getValue())}
          </div>
        ) : (
          ""
        )}
      </>
    ),
    muiEditTextFieldProps: ({ cell }) => ({
      type: "date",
    }),
  },
  {
    accessorKey: "revisedStart",
    header: `Revised Start`,
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <div style={{ whiteSpace: "wrap" }}>
            {convertDateString(cell.getValue())}
          </div>
        ) : (
          ""
        )}
      </>
    ),
    muiEditTextFieldProps: ({ cell }) => ({
      type: "date",
    }),
  },
  {
    accessorKey: "revisedEnd",
    header: `Revised End`,
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <div style={{ whiteSpace: "wrap" }}>
            {convertDateString(cell.getValue())}
          </div>
        ) : (
          ""
        )}
      </>
    ),
    muiEditTextFieldProps: ({ cell }) => ({
      type: "date",
    }),
  },

  {
    accessorKey: "task_members",
    header: `Responsible`,
    size: 100,
    enableSorting: false,
    Cell: ({ cell, row }) => {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {cell.getValue()
            ? cell
                .getValue()
                .map((user, index) => (
                  <Chip key={index} label={user} size="small" />
                ))
            : ""}
        </Box>
      );
    },
    muiEditTextFieldProps: ({ value, onChange }) => {
      // Combine plant people and leads for selection
      const allUsers = [
        ...(plant_people || []).map((user) => 
          typeof user === "object" ? user.name || user.email : user
        ),
        ...(plantLeads || [])
      ];
      
      return {
        children: allUsers.map((user, index) => (
          <MenuItem key={index} value={user}>
            {user}
          </MenuItem>
        )),
        select: true,
        SelectProps: {
          multiple: true,
          renderValue: (selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value, index) => (
                <Chip key={index} label={value} />
              ))}
            </Box>
          ),
        },
      };
    },
  },
  {
    accessorKey: "priority",
    header: "Priority",
    enableColumnFilter: false,
    size: 90,
    muiEditTextFieldProps: () => ({
      children: ["High", "Medium", "Low"].map((func) => (
        <MenuItem key={func} value={func}>
          {func}
        </MenuItem>
      )),
      select: true,
    }),
  },
   
        {
          accessorKey: "estimatedTime",
          header:fixedType === "procurement" ?"Budged Cost" : "Budget Hours",
          enableEditing: true,
          enableColumnFilter: false,
          enableSorting: false,
          size: 30,
        },
  {
    accessorKey: "hours",
    header: fixedType === "procurement" ? "$" :  "Actual Hours",
    enableEditing: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 30,
  },
  {
    accessorKey: "progress",
    header: "Progress",
    enableEditing: false,
    enableColumnFilter: false,
    size: 60,
    enableSorting: false,
    Cell: ({ cell, row }) => (
      <CircularProgressWithLabel value={cell.getValue()} />
    ),
  },
  {
    accessorKey: "status",
    header: `Status`,
    enableColumnFilter: false,
    size: 80,
    muiEditTextFieldProps: () => ({
      children: ["Not Started", "Progress", "Hold", "Completed", "Delayed"].map(
        (func) => (
          <MenuItem key={func} value={func}>
            {func}
          </MenuItem>
        )
      ),
      select: true,
    }),
    Cell: ({ cell }) => (
      <>
        {cell.getValue() ? (
          <Chip
            label={cell.getValue()}
            size="small"
            color={statusColorMap[cell.getValue()] || "default"}
          />
        ) : (
          <></>
        )}
      </>
    ),
  },
  {
    accessorKey: "chat",
    header: `Chat`,
    enableEditing: false,
    enableSorting: false,
    enableColumnFilter: false,
    size: 50,
    Header: () => <ChatIcon />,
    Cell: ({ row }) => (
      <IconButton
        onClick={() => handleCommentDialogOpen(row)}
      >
        <ChatIcon />
      </IconButton>
    ),
  },
  {
    accessorKey: "attachment",
    header: `Attachment`,
    enableEditing: false,
    enableSorting: false,
    enableColumnFilter: false,
    size: 20,
    Header: () => <AttachFileIcon />,
    Cell: ({ row }) => (
      <IconButton
        onClick={() => handleButtonClick("attachment", row)}
        disabled={
          plant_people?.some((user) => {
            const userName = typeof user === "object" ? user.name || user.email : user;
            return userName === currentUser;
          }) ||
          plantLeads?.includes(currentUser)
            ? false
            : true
        }
      >
        <AttachFileIcon />
      </IconButton>
    ),
  },
  {
    accessorKey: "timesheet",
    header: `Timesheet`,
    enableEditing: false,
    enableSorting: false,
    enableColumnFilter: false,
    size: 20,
    Header: () => <PostAddIcon />,
    Cell: ({ row }) => (
      <IconButton
        onClick={() => handleButtonClick("timesheet", row)}
      >
        <PostAddIcon />
      </IconButton>
    ),
  },
  ];
};

